import React, { useState, useEffect } from 'react';
import { UserPlus, Check, X, ArrowLeft, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface FollowRequest {
  id: string;
  requester: {
    id: string;
    name: string;
    avatar: string | null;
    role: string;
    sport: string;
  };
  message: string | null;
  requestedAt: string;
}

const FollowRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [followRequests, setFollowRequests] = useState<FollowRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState<string | null>(null);

  useEffect(() => {
    loadFollowRequests();
  }, []);

  const loadFollowRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/follow-requests');
      if (response.data.success) {
        setFollowRequests(response.data.data.followRequests || []);
      }
    } catch (error: any) {
      console.error('Failed to load follow requests:', error);
      toast.error('Failed to load follow requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requesterId: string, requestId: string) => {
    try {
      setProcessingRequest(requestId);
      const response = await api.post(`/users/${requesterId}/follow/accept`);
      
      if (response.data.success) {
        // Remove from list
        setFollowRequests(prev => prev.filter(req => req.id !== requestId));
        toast.success('Follow request accepted!');
        
        // Navigate to chat if created
        if (response.data.data.chat) {
          setTimeout(() => {
            navigate(`/chats/${response.data.data.chat.id}`);
            toast.success('Chat opened!');
          }, 500);
        }
      }
    } catch (error: any) {
      console.error('Failed to accept follow request:', error);
      const errorMessage = error.response?.data?.error?.message || 'Failed to accept follow request';
      
      // If request not found, it might have been already processed
      if (errorMessage.includes('not found') || error.response?.status === 404) {
        // Remove from list since request is already processed
        setFollowRequests(prev => prev.filter(req => req.id !== requestId));
        toast('Follow request was already processed', { icon: 'ℹ️' });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleReject = async (requesterId: string, requestId: string) => {
    try {
      setProcessingRequest(requestId);
      const response = await api.post(`/users/${requesterId}/follow/reject`);
      
      if (response.data.success) {
        // Remove from list
        setFollowRequests(prev => prev.filter(req => req.id !== requestId));
        toast.success('Follow request rejected');
      }
    } catch (error: any) {
      console.error('Failed to reject follow request:', error);
      const errorMessage = error.response?.data?.error?.message || 'Failed to reject follow request';
      
      // If request not found, it might have been already processed
      if (errorMessage.includes('not found') || error.response?.status === 404) {
        // Remove from list since request is already processed
        setFollowRequests(prev => prev.filter(req => req.id !== requestId));
        toast('Follow request was already processed', { icon: 'ℹ️' });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setProcessingRequest(null);
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading follow requests..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-2">
              <UserPlus className="w-6 h-6 text-blue-500" />
              <h1 className="text-xl font-bold text-gray-900">Follow Requests</h1>
            </div>
            {followRequests.length > 0 && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                {followRequests.length}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {followRequests.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No follow requests</h3>
            <p className="text-gray-500">You don't have any pending follow requests.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {followRequests.map((request) => (
              <div
                key={request.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6"
              >
                <div className="flex items-start space-x-4">
                  {/* Avatar */}
                  <div
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm sm:text-base font-medium cursor-pointer hover:opacity-80 flex-shrink-0"
                    onClick={() => navigate(`/profile/${request.requester.id}`)}
                  >
                    {request.requester.avatar ? (
                      <img
                        src={request.requester.avatar}
                        alt={request.requester.name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      getInitials(request.requester.name)
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3
                          className="text-base sm:text-lg font-semibold text-gray-900 cursor-pointer hover:text-blue-600"
                          onClick={() => navigate(`/profile/${request.requester.id}`)}
                        >
                          {request.requester.name}
                        </h3>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs sm:text-sm text-gray-500">{request.requester.role}</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-xs sm:text-sm text-gray-500">{request.requester.sport}</span>
                        </div>
                        {request.message && (
                          <p className="text-sm text-gray-600 mt-2 italic">
                            "{request.message}"
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          {formatTime(request.requestedAt)}
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-2 mt-4">
                      <button
                        onClick={() => handleAccept(request.requester.id, request.id)}
                        disabled={processingRequest === request.id}
                        className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                      >
                        <Check className="w-4 h-4" />
                        <span>Accept</span>
                      </button>
                      <button
                        onClick={() => handleReject(request.requester.id, request.id)}
                        disabled={processingRequest === request.id}
                        className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
                      >
                        <X className="w-4 h-4" />
                        <span>Reject</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowRequestsPage;

